import { PLAINTEXT_ID } from "../registry";
import type { DetectionStage } from "./types";

/** Lines scanned at the top and bottom of a file for shebangs and modelines. */
const MODELINE_SCAN_LINES = 5;

export const filenameStage: DetectionStage = ({ path }, registry) => {
  const lang = path ? registry.byFilename(path) : undefined;
  return lang && { id: lang.id, source: "filename", confidence: 1 };
};

export const extensionStage: DetectionStage = ({ path }, registry) => {
  const lang = path ? registry.byExtension(path) : undefined;
  return lang && { id: lang.id, source: "extension", confidence: 0.9 };
};

/**
 * Interpreter named by a shebang line: "#!/usr/bin/env -S deno run" → "deno",
 * "#!/usr/bin/python3.11 -u" → "python3.11". Undefined if the line isn't a shebang.
 */
export function shebangInterpreter(firstLine: string): string | undefined {
  if (!firstLine.startsWith("#!")) return undefined;
  const words = firstLine.slice(2).trim().split(/\s+/).filter(Boolean);
  let program = words.shift();
  if (program && basenameOf(program) === "env") {
    // Skip env's flags (-S, -i, -u NAME) and NAME=value assignments.
    while (words.length > 0) {
      const word = words[0] ?? "";
      if (word === "-u" || word === "--unset") words.splice(0, 2);
      else if (word.startsWith("-") || word.includes("=")) words.shift();
      else break;
    }
    program = words.shift();
  }
  return program ? basenameOf(program) : undefined;
}

function basenameOf(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/** "python3.11" → ["python3.11", "python3", "python"]: progressively drop version suffixes. */
function interpreterCandidates(interpreter: string): string[] {
  const candidates = [interpreter];
  const withoutDotted = interpreter.replace(/[\d.]*\d$/, "");
  const withoutMinor = interpreter.replace(/\.[\d.]*$/, "");
  for (const candidate of [withoutMinor, withoutDotted]) {
    if (candidate && !candidates.includes(candidate)) candidates.push(candidate);
  }
  return candidates;
}

export const shebangStage: DetectionStage = ({ content }, registry) => {
  const firstLine = content?.slice(0, content.indexOf("\n") >>> 0).trimEnd() ?? "";
  const interpreter = shebangInterpreter(firstLine);
  if (!interpreter) return undefined;
  for (const candidate of interpreterCandidates(interpreter)) {
    const lang = registry.byInterpreter(candidate) ?? registry.get(candidate);
    if (lang) return { id: lang.id, source: "shebang", confidence: 0.95 };
  }
  return undefined;
};

const VIM_MODELINE = /(?:^|\s)(?:vi|vim|ex)(?:[<=>]?\d+)?:.*?(?:ft|filetype|syntax|syn)=([\w.+-]+)/;
const EMACS_MODELINE = /-\*-(.*?)-\*-/;

/** Language named by a Vim or Emacs modeline in `line`, as written (not yet resolved). */
export function modelineLanguage(line: string): string | undefined {
  const vim = VIM_MODELINE.exec(line);
  if (vim?.[1]) return vim[1];
  const emacs = EMACS_MODELINE.exec(line)?.[1]?.trim();
  if (!emacs) return undefined;
  if (!emacs.includes(":")) return emacs; // "-*- python -*-"
  const mode = /(?:^|;)\s*mode\s*:\s*([\w.+-]+)/i.exec(emacs);
  return mode?.[1];
}

/** Emacs modes often carry a "-mode" suffix or "js2"-style variants. */
const MODE_SUFFIX = /(?:-ts)?-mode$/;

export const modelineStage: DetectionStage = ({ content }, registry) => {
  if (!content) return undefined;
  const lines = content.split("\n");
  const scanned = [
    ...lines.slice(0, MODELINE_SCAN_LINES),
    ...lines.slice(Math.max(MODELINE_SCAN_LINES, lines.length - MODELINE_SCAN_LINES)),
  ];
  for (const line of scanned) {
    const name = modelineLanguage(line);
    if (!name) continue;
    const lang = registry.get(name) ?? registry.get(name.replace(MODE_SUFFIX, ""));
    if (lang) return { id: lang.id, source: "modeline", confidence: 0.95 };
  }
  return undefined;
};

export const plaintextStage: DetectionStage = () => ({
  id: PLAINTEXT_ID,
  source: "default",
  confidence: 0,
});

/** The deterministic pipeline, in priority order. */
export const DEFAULT_STAGES: readonly DetectionStage[] = [
  filenameStage,
  extensionStage,
  shebangStage,
  modelineStage,
  plaintextStage,
];
